import { useState, useCallback, useEffect, useMemo } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Constraint type definition from API
 */
export interface ConstraintTypeDefinition {
  id: string
  code: string
  name: string
  description: string | null
  category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
  is_hard_default: boolean
  default_priority: number
  default_weight: number
  parameter_schema: Record<string, unknown> | null
}

/**
 * Hook result interface
 */
interface UseConstraintTypesResult {
  /** List of available constraint types */
  constraintTypes: ConstraintTypeDefinition[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Refetch constraint types */
  refetch: () => Promise<void>
  /** Get constraint type by code */
  getTypeByCode: (code: string) => ConstraintTypeDefinition | undefined
  /** Get constraint types by category */
  getTypesByCategory: (
    category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
  ) => ConstraintTypeDefinition[]
  /** Get hard constraint types (default is_hard = true) */
  hardConstraintTypes: ConstraintTypeDefinition[]
  /** Get soft constraint types (default is_hard = false) */
  softConstraintTypes: ConstraintTypeDefinition[]
}

/**
 * Constraint type codes currently supported in the UI.
 * Only these types will be shown in the Add Constraint dropdown.
 */
const ALLOWED_CONSTRAINT_CODES = [
  'min_rest_days',
  'venue_capacity',
  'max_games_per_team_per_day',
  'games_per_team_per_week',
]

/**
 * Custom hook for fetching and managing scheduler constraint types
 *
 * @returns Object containing constraint types data, loading states, and helper functions
 */
export const useConstraintTypes = (): UseConstraintTypesResult => {
  const [constraintTypes, setConstraintTypes] = useState<
    ConstraintTypeDefinition[]
  >([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  /**
   * Fetch all constraint types
   */
  const fetchConstraintTypes = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const { data } = await req('/scheduler/constraint-types')
      // Only include constraint types that are in the allowed list
      const filteredData = (data || []).filter(
        (type: ConstraintTypeDefinition) =>
          ALLOWED_CONSTRAINT_CODES.includes(type.code)
      )
      setConstraintTypes(filteredData)
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching constraint types')
      )
      setConstraintTypes([])
    } finally {
      setLoading(false)
    }
  }, [])

  /**
   * Get constraint type by code
   */
  const getTypeByCode = useCallback(
    (code: string): ConstraintTypeDefinition | undefined => {
      return constraintTypes.find((t) => t.code === code)
    },
    [constraintTypes]
  )

  /**
   * Get constraint types by category
   */
  const getTypesByCategory = useCallback(
    (
      category: 'venue' | 'time' | 'fairness' | 'preference' | 'distribution'
    ): ConstraintTypeDefinition[] => {
      return constraintTypes.filter((t) => t.category === category)
    },
    [constraintTypes]
  )

  // Hard constraint types (default is_hard = true)
  const hardConstraintTypes = useMemo(
    () => constraintTypes.filter((t) => t.is_hard_default),
    [constraintTypes]
  )

  // Soft constraint types (default is_hard = false)
  const softConstraintTypes = useMemo(
    () => constraintTypes.filter((t) => !t.is_hard_default),
    [constraintTypes]
  )

  // Fetch constraint types on mount
  useEffect(() => {
    fetchConstraintTypes()
  }, [fetchConstraintTypes])

  return {
    constraintTypes,
    loading,
    error,
    refetch: fetchConstraintTypes,
    getTypeByCode,
    getTypesByCategory,
    hardConstraintTypes,
    softConstraintTypes,
  }
}

export default useConstraintTypes
